/**
 * Browser console logging gated for production builds.
 *
 * Debug/info/warn are suppressed unless Vite dev mode or VITE_CLIENT_DEBUG_LOG=true.
 * Errors always log a short message; extra detail (stack, API bodies) only in debug mode.
 */

function isClientDebugLogEnabled(): boolean {
  return (
    import.meta.env.DEV ||
    String(import.meta.env.VITE_CLIENT_DEBUG_LOG ?? '').toLowerCase() === 'true'
  );
}

/** Verbose diagnostics (policy payloads, claim wire format, API traces). */
export function clientLogDebug(...args: unknown[]): void {
  if (isClientDebugLogEnabled()) {
    console.log(...args);
  }
}

/** Optional last argument to {@link clientLogInfo} to bypass the global debug gate. */
export interface ClientLogInfoOptions {
  readonly __clientLogInfoOptions: true;
  /** When true, logs even if `VITE_CLIENT_DEBUG_LOG` is unset (e.g. FNOL wire override). */
  force?: boolean;
}

/** Pass as the last argument to {@link clientLogInfo} to force logging in production. */
export function clientLogInfoOptions(force = true): ClientLogInfoOptions {
  return { __clientLogInfoOptions: true, force };
}

function isClientLogInfoOptions(value: unknown): value is ClientLogInfoOptions {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as ClientLogInfoOptions).__clientLogInfoOptions === true
  );
}

/** Same as debug; use for console.info-style traces. */
export function clientLogInfo(...args: unknown[]): void {
  const last = args[args.length - 1];
  if (isClientLogInfoOptions(last)) {
    const logArgs = args.slice(0, -1);
    if (last.force || isClientDebugLogEnabled()) {
      console.info(...logArgs);
    }
    return;
  }
  if (isClientDebugLogEnabled()) {
    console.info(...args);
  }
}

/** Non-fatal diagnostics (empty policy list hints, validation blocks during dev). */
export function clientLogWarn(...args: unknown[]): void {
  if (isClientDebugLogEnabled()) {
    console.warn(...args);
  }
}

/**
 * Failures visible in production — message only unless debug logging is enabled.
 */
export function clientLogError(message: string, detail?: unknown): void {
  if (isClientDebugLogEnabled()) {
    if (detail !== undefined) {
      console.error(message, detail);
    } else {
      console.error(message);
    }
    return;
  }
  console.error(message);
}
